import type APIAddress from '~/types/APIAddress';

import { type AddressBookAction, type AddressHash } from '~/reducers/address/address.reducer';
import { type SavedAPIAddress } from '~/types/APIAddress';
import { type TrackingReference } from '~/utils/TrackingHelper/types';

import { ActionKeys } from './constants';

export interface FetchAddressesPayload {
  addresses: SavedAPIAddress[];
}

interface FetchAddressesErrorPayload {
  error: string;
}

interface CreateAPIAddressPayloadBase {
  addressBookName?: string;
  key?: string;
}

export interface CreateAPIAddressPayload extends CreateAPIAddressPayloadBase {
  address: APIAddress;
  trackingReference: TrackingReference;
}

export interface CreateAPIAddressSuccessPayload extends CreateAPIAddressPayloadBase {
  address: SavedAPIAddress;
  trackingReference: TrackingReference;
}

// Edit
interface CreateAPIAddressErrorPayload {
  error: string;
}

export interface EditAPIAddressPayload {
  address: APIAddress;
  id?: number;
}

interface EditAPIAddressErrorPayload {
  error: string;
}

export interface DeleteAPIAddressPayload {
  id: number;
  key?: Maybe<string>;
}

interface DeleteAPIAddressErrorPayload {
  error: string;
}

// Fetch
export const fetchAddresses = () =>
  <const>{
    type: ActionKeys.ADDRESSES_FETCH,
  };

export const fetchAddressesSuccess = (payload: FetchAddressesPayload) =>
  <const>{
    payload,
    type: ActionKeys.ADDRESSES_FETCH_SUCCESS,
  };

export const fetchAddressesError = (payload: FetchAddressesErrorPayload) =>
  <const>{
    payload,
    type: ActionKeys.ADDRESSES_FETCH_ERROR,
  };

// Create
export const createAPIAddress = (payload: CreateAPIAddressPayload) =>
  <const>{
    payload,
    type: ActionKeys.APIADDRESS_CREATE,
  };

export const createAPIAddressSuccess = (payload: CreateAPIAddressSuccessPayload) =>
  <const>{
    payload,
    type: ActionKeys.APIADDRESS_CREATE_SUCCESS,
  };

export const createAPIAddressError = (payload: CreateAPIAddressErrorPayload) =>
  <const>{
    payload,
    type: ActionKeys.APIADDRESS_CREATE_ERROR,
  };

// Edit
export const editAPIAddress = (payload: EditAPIAddressPayload) =>
  <const>{
    payload,
    type: ActionKeys.APIADDRESS_EDIT,
  };

export const editAPIAddressSuccess = (payload: EditAPIAddressPayload) =>
  <const>{
    payload,
    type: ActionKeys.APIADDRESS_EDIT_SUCCESS,
  };

export const editAPIAddressError = (payload: EditAPIAddressErrorPayload) =>
  <const>{
    payload,
    type: ActionKeys.APIADDRESS_EDIT_ERROR,
  };

// Delete
export const deleteAPIAddress = (payload: DeleteAPIAddressPayload) =>
  <const>{
    payload,
    type: ActionKeys.APIADDRESS_DELETE,
  };

export const deleteAPIAddressSuccess = (id: number) =>
  <const>{
    payload: {
      id,
    },
    type: ActionKeys.APIADDRESS_DELETE_SUCCESS,
  };

export const deleteAPIAddressError = (payload: DeleteAPIAddressErrorPayload) =>
  <const>{
    payload,
    type: ActionKeys.APIADDRESS_DELETE_ERROR,
  };

export const clearAPIAddressError = () =>
  <const>{
    type: ActionKeys.APIADDRESS_CLEAR_ERROR,
  };

export const addressBookInitialize = (
  addressBookName: string,
  addresses: AddressHash,
  combineSenderReturnAddresses?: boolean, // Using this parameter will manually configure combineSenderReturnAddresses instead of letting the reducer decide
) =>
  <const>{
    payload: {
      addressBookName,
      addresses,
      combineSenderReturnAddresses,
    },
    type: ActionKeys.ADDRESS_BOOK_INITIALIZE,
  };

export const addressBookPerformAction = (
  addressBookName: string,
  action: Maybe<AddressBookAction>,
) =>
  <const>{
    payload: {
      action,
      addressBookName,
    },
    type: ActionKeys.ADDRESS_BOOK_ACTION,
  };

export const addressBookSelectAddress = (
  addressBookName: string,
  key: string,
  address: APIAddress | number,
) =>
  <const>{
    payload: {
      address,
      addressBookName,
      key,
    },
    type: ActionKeys.ADDRESS_BOOK_SELECT,
  };

export const addressBookClearSelection = (addressBookName: string, key: string) =>
  <const>{
    payload: {
      addressBookName,
      key,
    },
    type: ActionKeys.ADDRESS_BOOK_CLEAR_SELECTION,
  };

export const addressBookSelectDefaultAddress = (addressBookName: string, key: string) =>
  <const>{
    payload: {
      addressBookName,
      key,
    },
    type: ActionKeys.ADDRESS_BOOK_SELECT_DEFAULT,
  };

export const toggleCombineSenderReturnAddresses = (addressBookName: string) =>
  <const>{
    payload: {
      addressBookName,
    },
    type: ActionKeys.ADDRESS_BOOK_TOGGLE_COMBINE_SENDER_RETURN_ADDRESSES,
  };

export const addressBookClearState = (addressBookName: string) =>
  <const>{
    payload: {
      addressBookName,
    },
    type: ActionKeys.ADDRESS_BOOK_CLEAR_STATE,
  };

export type AddressActions =
  | ReturnType<typeof addressBookClearSelection>
  | ReturnType<typeof addressBookClearState>
  | ReturnType<typeof addressBookInitialize>
  | ReturnType<typeof addressBookPerformAction>
  | ReturnType<typeof addressBookSelectAddress>
  | ReturnType<typeof addressBookSelectDefaultAddress>
  | ReturnType<typeof clearAPIAddressError>
  | ReturnType<typeof createAPIAddress>
  | ReturnType<typeof createAPIAddressError>
  | ReturnType<typeof createAPIAddressSuccess>
  | ReturnType<typeof deleteAPIAddress>
  | ReturnType<typeof deleteAPIAddressError>
  | ReturnType<typeof deleteAPIAddressSuccess>
  | ReturnType<typeof editAPIAddress>
  | ReturnType<typeof editAPIAddressError>
  | ReturnType<typeof editAPIAddressSuccess>
  | ReturnType<typeof fetchAddresses>
  | ReturnType<typeof fetchAddressesError>
  | ReturnType<typeof fetchAddressesSuccess>
  | ReturnType<typeof toggleCombineSenderReturnAddresses>;
